import IconButton from "components/icon-button";
import moment from "moment-timezone";
import React, {
  forwardRef,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
} from "react";
import { Button, FormControl } from "react-bootstrap";

import ReactDatePicker, { ReactDatePickerProps } from "react-datepicker";

import "./DatePicker.css";
import { ReactComponent as OutlineCalendar } from "assets/svg/outline-calendar.svg";
import { ReactComponent as ChevronLeft } from "assets/svg/outline-chevron-left-16.svg";
import { ReactComponent as ChevronRight } from "assets/svg/outline-chevron-right-16.svg";
import getClasses from "utils/getClasses";
import styles from "./DatePicker.module.scss";
import { createPortal } from "react-dom";
import messages from "messages";

type Props = Omit<ReactDatePickerProps, "selected" | "minDate" | "maxDate"> & {
  selected?: Date;
  minDate?: Date;
  maxDate?: Date;
  timezone: TimeZone;
  returnStartOfDay?: boolean;
  validityMessage?: string;
};
const DatePicker = forwardRef<
  Pick<HTMLFormElement, "checkValidity" | "reset">,
  Props
>(function ForwardedDatePicker(
  {
    selected,
    minDate,
    maxDate,
    onChange,
    timezone,
    returnStartOfDay,
    dateFormat,
    wrapperClassName,
    disabled,
    required,
    validityMessage,
    ...props
  },
  ref,
) {
  const userToLocal = useCallback(
    (date: Date | undefined) => {
      if (!date) {
        return undefined;
      }

      const dateInUserSettingsTimezone = moment
        .tz(date, timezone)
        .format("YYYY-MM-DD");
      const dateInBrowserLocalTimezone = new Date(
        `${dateInUserSettingsTimezone}T00:00:00`,
      );

      return dateInBrowserLocalTimezone;
    },
    [timezone],
  );

  const localToUser = useCallback(
    (date: Date | undefined) => {
      if (!date) {
        return undefined;
      }

      const dateInBrowserLocalTimezone = moment(date).format("YYYY-MM-DD");
      const dateInUserSettingsTimezone = moment
        .tz(`${dateInBrowserLocalTimezone}T00:00:00`, timezone)
        .toDate();

      return dateInUserSettingsTimezone;
    },
    [timezone],
  );

  // invalid should receive both from prop and internal validation result
  const [isInvalid, setIsInvalid] = useState<boolean | undefined>(undefined);
  // implement a HTML5 form like validation functionality
  const checkValidity = useCallback(
    (val = selected) => {
      if (required) {
        if (!val) {
          setIsInvalid(true);
          return false;
        }
      }

      if (minDate && val) {
        if (val < minDate) {
          setIsInvalid(true);
          return false;
        }
      }

      if (maxDate && val) {
        if (val > maxDate) {
          setIsInvalid(true);
          return false;
        }
      }

      setIsInvalid(false);
      return true;
    },
    [maxDate, minDate, required, selected],
  );

  useImperativeHandle(ref, () => ({
    checkValidity,
    reset: () => setIsInvalid(undefined),
  }));

  const { currentMonth, currentYear } = useMemo(() => {
    const now = moment.tz(timezone);
    return {
      currentMonth: now.month(),
      currentYear: now.year(),
    };
  }, [timezone]);

  const handleChange = (date: Date, event) => {
    const dateInUserTimezone = localToUser(date);

    const adjustedDate = returnStartOfDay
      ? moment.tz(dateInUserTimezone, timezone).startOf("day").toDate()
      : moment.tz(dateInUserTimezone, timezone).endOf("day").toDate();

    onChange(adjustedDate, event);

    if (isInvalid === true) {
      // try to resolve invalid value
      checkValidity(adjustedDate);
    }
  };

  const CustomInput = useMemo(
    () =>
      forwardRef(({ value, onClick, placeholder }: any, ref: any) => (
        <Button
          ref={ref}
          className={getClasses(
            styles["date-picker__input"],
            !value && styles["date-picker__input--no-value"],
            isInvalid && styles["date-picker__input--invalid"],
          )}
          variant="link"
          onClick={onClick}
          disabled={disabled}
        >
          <span>{value || placeholder || "mm/dd/yyyy"}</span>
          <OutlineCalendar className={styles["date-picker__icon"]} />
        </Button>
      )),
    [disabled, isInvalid],
  );

  const renderDayContents = useCallback(
    (day: string | number, date: Date) => {
      const isToday =
        moment(date).format("L") === moment().tz(timezone).format("L");

      return (
        <span
          className={getClasses(
            "react-datepicker__day-text",
            isToday ? "react-datepicker__day--is-today" : "",
          )}
        >
          {day}
        </span>
      );
    },
    [timezone],
  );

  const renderCustomHeader = useCallback(
    ({
      date,
      changeMonth,
      changeYear,
      decreaseMonth,
      increaseMonth,
      prevMonthButtonDisabled,
      nextMonthButtonDisabled,
    }) => (
      <div className={styles["date-picker__calendar-header"]}>
        {/* month picker */}
        <IconButton onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
          <ChevronLeft />
        </IconButton>

        <div className="md-16 mx-3">{moment(date).format("MMM, yyyy")}</div>

        <IconButton onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
          <ChevronRight />
        </IconButton>
        {/* today link button */}
        <Button
          variant={"link"}
          className={styles["date-picker__calendar-header-today"]}
          onClick={() => {
            changeMonth(currentMonth);
            changeYear(currentYear);
          }}
        >
          {messages.today}
        </Button>
      </div>
    ),
    [currentMonth, currentYear],
  );

  return (
    <>
      <ReactDatePicker
        {...props}
        selected={userToLocal(selected)}
        minDate={userToLocal(minDate)}
        maxDate={userToLocal(maxDate)}
        renderCustomHeader={renderCustomHeader}
        showPopperArrow={false}
        calendarClassName={styles["date-picker__calendar"]}
        dateFormat={dateFormat || "ccc, MMM d, yyyy"}
        formatWeekDay={(day) => day.slice(0, 1)}
        popperClassName={styles["date-picker__popper"]}
        popperContainer={CalendarContainer}
        popperModifiers={{
          preventOverflow: {
            boundariesElement: "viewport",
          },
        }}
        onChange={handleChange}
        customInput={<CustomInput />}
        renderDayContents={renderDayContents}
        wrapperClassName={wrapperClassName}
      />
      {isInvalid && (
        <FormControl.Feedback
          className={styles["date-picker__invalid-feedback"]}
          type={"invalid"}
          aria-live={"polite"}
        >
          {validityMessage || messages.selectADate}
        </FormControl.Feedback>
      )}
    </>
  );
});

export default DatePicker;

const CalendarContainer = ({ children }) =>
  children
    ? createPortal(
        React.cloneElement(children, {
          className: `${styles.popper} react-datepicker-popper`,
        }),
        document.body,
      )
    : null;
